// frontend/src/components/IssueDialog.js

import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Typography, Stack } from '@mui/material';
import { addOrderIssue, getIssueStatuses } from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';

const IssueDialog = ({ open, onClose, order, onIssueAdded }) => {
    const { showSnackbar } = useSnackbar();
    const [statusId, setStatusId] = useState('');
    const [comments, setComments] = useState('');
    const [issueStatuses, setIssueStatuses] = useState([]);

    useEffect(() => {
        const fetchIssueStatuses = async () => {
            try {
                const data = await getIssueStatuses();
                if (data.success && Array.isArray(data.statuses)) {
                    setIssueStatuses(data.statuses);
                } else {
                    console.error('Unexpected response structure:', data);
                    showSnackbar('Failed to fetch issue statuses.', 'error');
                }
            } catch (error) {
                console.error('Error fetching issue statuses:', error);
                showSnackbar('Failed to fetch issue statuses.', 'error');
            }
        };
        fetchIssueStatuses();
    }, [showSnackbar]);

    useEffect(() => {
        if (issueStatuses.length > 0) {
            if (order.issueStatus) {
                const status = issueStatuses.find(s => s.statusName === order.issueStatus);
                if (status) {
                    setStatusId(status.statusId);
                }
                setComments(order.comments || '');
            } else {
                setStatusId('');
                setComments('');
            }
        }
    }, [order, issueStatuses]);

    const handleSubmit = async () => {
        try {
            const selectedStatus = issueStatuses.find((status) => String(status.statusId) === String(statusId));
            const selectedStatusName = selectedStatus?.statusName;
            const currentOrderStatus = order?.orderStatus;

            if (selectedStatusName === 'On Hold' && currentOrderStatus && currentOrderStatus !== 'New') {
                const restrictionMessage = currentOrderStatus === 'Dispatched'
                    ? 'Issue status cannot be set to On Hold because the order has been Dispatched.'
                    : `Issue status cannot be set to On Hold because the order is currently ${currentOrderStatus}. Only New orders can be placed On Hold.`;
                showSnackbar(restrictionMessage, 'warning');
                return;
            }

            await addOrderIssue(order.orderId, { statusId, comments });
            onClose();
            onIssueAdded();
            showSnackbar('Issue saved successfully', 'success');
        } catch (error) {
            console.error('Error adding/updating issue:', error);
            showSnackbar(error.response?.data?.message || error.message || 'Error adding/updating issue', 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{order.issueStatus ? 'Update Order Issue' : 'Add Order Issue'}</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Order: <Typography component="span" variant="body2" color="text.secondary">{order.orderNumber}</Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Capture the current issue status and leave a short note for the next operator if needed.
                    </Typography>
                    <TextField
                        select
                        label="Issue Status"
                        value={statusId}
                        onChange={(e) => setStatusId(e.target.value)}
                        fullWidth
                        margin="normal"
                    >
                        {issueStatuses.map((status) => (
                            <MenuItem key={status.statusId} value={status.statusId}>
                                {status.statusName}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Operator Notes"
                        placeholder="Add a short note about the issue, blocker, or next step"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        margin="normal"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!statusId}>
                    Save Issue
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default IssueDialog;
